/**
 * 通知先（Webhook）の管理画面（課題 #113）で使う定義と判定。
 *
 * 通知の本文には個人を特定できる情報は入らない（件数と種別だけ。#71-4）。
 * 画面では「何が起きたときに通知されるか」を選んでもらう。
 */
import type { WebhookCreateRequest, WebhookKind } from '../api/client';

/**
 * 画面に並べる種別の順。Chatwork を先頭にする（2026-09-29 高尾さん: CENTRIC は Slack・Teams を使わず、
 * Chatwork で受け取る。BE PR #141）
 */
export const WEBHOOK_KINDS: readonly WebhookKind[] = ['chatwork', 'slack', 'teams', 'generic'];

/**
 * 通知する事象。BE の配信（app/services/chat_notification.py の _TITLES）と同じ名前にする。
 * 名前が違うと、登録はできても通知が 1 通も届かない（BE は登録された事象名で絞るため）。
 */
export const WEBHOOK_EVENTS: readonly string[] = ['mcs_alert.detected', 'upload_failure', 'stt_failure'];

/** 画面の事象名のキー。BE が知らない事象を返したら null（生の名前を出す） */
export function webhookEventKey(event: string): string | null {
  return WEBHOOK_EVENTS.includes(event) ? event.replace('.', '_') : null;
}

/** Chatwork の API トークンの長さ（BE の検証と同じ） */
export const CHATWORK_TOKEN_MIN = 10;
export const CHATWORK_TOKEN_MAX = 200;

/**
 * Chatwork のルーム ID を取り出す。数字だけでも、ルームの URL（…/#!rid123456、
 * メッセージへのリンク …/#!rid123456-789 を含む）を貼っても受け付ける。読めなければ null
 */
export function normalizeChatworkRoomId(input: string): string | null {
  const v = input.trim();
  if (/^\d{1,20}$/.test(v)) return v;
  const m = /(?:^|[#!/])rid(\d{1,20})(?!\d)/.exec(v);
  return m ? m[1] : null;
}

export interface WebhookFormInput {
  kind?: WebhookKind;
  url: string;
  events: string[];
  chatworkRoomId?: string;
  chatworkToken?: string;
}

export type WebhookFormError =
  | 'urlRequired'
  | 'urlInvalid'
  | 'roomIdRequired'
  | 'roomIdInvalid'
  | 'tokenRequired'
  | 'tokenInvalid'
  | 'eventsRequired';

/** 登録前の入力チェック。問題が無ければ null、あれば翻訳キーの末尾を返す */
export function validateWebhookForm(input: WebhookFormInput): WebhookFormError | null {
  if (input.kind === 'chatwork') {
    const room = (input.chatworkRoomId ?? '').trim();
    if (!room) return 'roomIdRequired';
    if (!normalizeChatworkRoomId(room)) return 'roomIdInvalid';
    const token = (input.chatworkToken ?? '').trim();
    if (!token) return 'tokenRequired';
    if (token.length < CHATWORK_TOKEN_MIN || token.length > CHATWORK_TOKEN_MAX || /\s/.test(token)) {
      return 'tokenInvalid';
    }
  } else {
    const url = input.url.trim();
    if (!url) return 'urlRequired';
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return 'urlInvalid';
    } catch {
      return 'urlInvalid';
    }
  }
  if (input.events.length === 0) return 'eventsRequired';
  return null;
}

/**
 * 登録の要求を組み立てる（入力チェックを通った後に呼ぶ）。
 * Chatwork には url を送らず、ほかの種別にはルーム ID・トークンを送らない（BE はどちらも 422 にする）
 */
export function buildWebhookCreateBody(
  input: Required<Pick<WebhookFormInput, 'kind'>> & WebhookFormInput & { label?: string }
): WebhookCreateRequest {
  const label = input.label?.trim();
  const common = { events: input.events, kind: input.kind, verify: true, ...(label ? { label } : {}) };
  if (input.kind === 'chatwork') {
    return {
      ...common,
      chatwork_room_id: normalizeChatworkRoomId(input.chatworkRoomId ?? '') ?? '',
      chatwork_api_token: (input.chatworkToken ?? '').trim(),
    };
  }
  return { ...common, url: input.url.trim() };
}
